// Formatting and locale names on `Intl`, with date-fns-compatible tokens.

import { isValidDate, pad2, type WeekStart } from "./dateCore";

/** BCP-47 tag, e.g. `"en-GB"`. `undefined` means the runtime default. */
export type LocaleTag = string | undefined;

/* Intl caching */

// Intl.DateTimeFormat is expensive, so cache by (locale, options).
const formatterCache = new Map<string, Intl.DateTimeFormat>();

function getFormatter(locale: LocaleTag, options: Intl.DateTimeFormatOptions): Intl.DateTimeFormat {
  const key = `${locale ?? ""}|${JSON.stringify(options)}`;
  let formatter = formatterCache.get(key);
  if (!formatter) {
    try {
      formatter = new Intl.DateTimeFormat(locale || undefined, options);
    } catch {
      formatter = new Intl.DateTimeFormat(undefined, options);
    }
    formatterCache.set(key, formatter);
  }
  return formatter;
}

/* Locale names */

const monthNameCache = new Map<string, string[]>();

/** The 12 month names for a locale, January first. */
export function getMonthNames(locale: LocaleTag, style: "long" | "short" = "long"): string[] {
  const key = `${locale ?? ""}|${style}`;
  let names = monthNameCache.get(key);
  if (!names) {
    const formatter = getFormatter(locale, { month: style });
    // 2021 is arbitrary -- any non-leap year works, only the month matters.
    names = Array.from({ length: 12 }, (_, i) => formatter.format(new Date(2021, i, 1)));
    monthNameCache.set(key, names);
  }
  return names;
}

const weekdayNameCache = new Map<string, string[]>();

/** Weekday names from `weekStartsOn`, aligned index-for-index with the grid. */
export function getWeekdayNames(
  locale: LocaleTag,
  weekStartsOn: WeekStart = 0,
  style: "long" | "short" | "narrow" = "short",
): string[] {
  const key = `${locale ?? ""}|${weekStartsOn}|${style}`;
  let names = weekdayNameCache.get(key);
  if (!names) {
    const formatter = getFormatter(locale, { weekday: style });
    // 2023-01-01 was a Sunday, so index 0 of this run is day 0.
    names = Array.from({ length: 7 }, (_, i) =>
      formatter.format(new Date(2023, 0, 1 + ((weekStartsOn + i) % 7))),
    );
    weekdayNameCache.set(key, names);
  }
  return names;
}

/** The locale's first weekday; `getWeekInfo` is a method or property by engine. */
export function getDefaultWeekStart(locale: LocaleTag): WeekStart {
  if (!locale) return 0;
  try {
    const instance = new Intl.Locale(locale) as Intl.Locale & {
      getWeekInfo?: () => { firstDay?: number };
      weekInfo?: { firstDay?: number };
    };
    const info = instance.getWeekInfo?.() ?? instance.weekInfo;
    const firstDay = info?.firstDay;
    if (typeof firstDay !== "number") return 0;
    // Intl uses ISO numbering (1 = Monday ... 7 = Sunday); we use 0 = Sunday.
    return (firstDay % 7) as WeekStart;
  } catch {
    return 0;
  }
}

/* Caption options */

export interface MonthOption {
  label: string;
  index: number;
}

export interface CaptionOptions {
  years: number[];
  months: MonthOption[];
}

export interface CaptionOptionsInput {
  locale: LocaleTag;
  year: number;
  monthIndex: number;
  navStart: Date;
  navEnd: Date;
}

/** Widest year list a malformed bound may produce, either side of the view year. */
const MAX_YEAR_SPAN = 200;

const clampInt = (value: number, min: number, max: number) =>
  Number.isInteger(value) ? Math.min(max, Math.max(min, value)) : min;

/** Caption dropdown lists: never empty, always containing the displayed value. */
export function buildCaptionOptions({
  locale,
  year,
  monthIndex,
  navStart,
  navEnd,
}: CaptionOptionsInput): CaptionOptions {
  const anchorYear = Number.isFinite(year) ? year : new Date().getFullYear();
  const first = isValidDate(navStart) ? navStart.getFullYear() : anchorYear;
  const last = isValidDate(navEnd) ? navEnd.getFullYear() : anchorYear;
  // min/max, not first..last, so an inverted or wild bound still yields a list.
  const firstYear = Math.max(Math.min(first, last, anchorYear), anchorYear - MAX_YEAR_SPAN);
  const lastYear = Math.min(Math.max(first, last, anchorYear), anchorYear + MAX_YEAR_SPAN);

  const anchorMonth = clampInt(monthIndex, 0, 11);
  let lower = isValidDate(navStart) && year === navStart.getFullYear() ? navStart.getMonth() : 0;
  let upper = isValidDate(navEnd) && year === navEnd.getFullYear() ? navEnd.getMonth() : 11;
  if (lower > upper) {
    lower = 0;
    upper = 11;
  }
  lower = Math.min(lower, anchorMonth);
  upper = Math.max(upper, anchorMonth);

  return {
    years: Array.from({ length: lastYear - firstYear + 1 }, (_, i) => firstYear + i),
    months: getMonthNames(locale, "long")
      .map((label, index) => ({ label, index }))
      .filter((entry) => entry.index >= lower && entry.index <= upper),
  };
}


/* Token formatter */

// Longest tokens first; the leading alternative captures quoted literal text.
const TOKEN_PATTERN = /''|'([^']*)'|yyyy|yy|MMMM|MMM|MM|M|dd|d|EEEE|EEE|HH|H|hh|h|mm|m|ss|a/g;

/** Format with date-fns tokens: `yyyy yy MMMM MMM MM M dd d EEEE EEE HH H hh h mm m ss a`. */
export function formatDate(date: Date, pattern: string, locale?: LocaleTag): string {
  if (!(date instanceof Date) || Number.isNaN(date.getTime())) return "";

  return pattern.replace(TOKEN_PATTERN, (token, quoted: string | undefined) => {
    if (token === "''") return "'";
    if (quoted !== undefined) return quoted;

    switch (token) {
      case "yyyy":
        return String(date.getFullYear()).padStart(4, "0");
      case "yy":
        return pad2(date.getFullYear() % 100);
      case "MMMM":
        return getMonthNames(locale, "long")[date.getMonth()];
      case "MMM":
        return getMonthNames(locale, "short")[date.getMonth()];
      case "MM":
        return pad2(date.getMonth() + 1);
      case "M":
        return String(date.getMonth() + 1);
      case "dd":
        return pad2(date.getDate());
      case "d":
        return String(date.getDate());
      case "EEEE":
        return getWeekdayNames(locale, 0, "long")[date.getDay()];
      case "EEE":
        return getWeekdayNames(locale, 0, "short")[date.getDay()];
      case "HH":
        return pad2(date.getHours());
      case "H":
        return String(date.getHours());
      case "hh":
        return pad2(date.getHours() % 12 || 12);
      case "h":
        return String(date.getHours() % 12 || 12);
      case "mm":
        return pad2(date.getMinutes());
      case "m":
        return String(date.getMinutes());
      case "ss":
        return pad2(date.getSeconds());
      case "a":
        return date.getHours() < 12 ? "AM" : "PM";
      default:
        return token;
    }
  });
}

/** Long month + year for the calendar caption and its live-region announcement. */
export function formatMonthCaption(month: Date, locale?: LocaleTag): string {
  // `.format(Invalid Date)` throws RangeError, so an invalid month must not reach it.
  if (!isValidDate(month)) return "";
  return getFormatter(locale, { month: "long", year: "numeric" }).format(month);
}

/** Full date, for a day cell's accessible name. */
export function formatDayLabel(date: Date, locale?: LocaleTag): string {
  if (!isValidDate(date)) return "";
  return getFormatter(locale, {
    weekday: "long",
    day: "numeric",
    month: "long",
    year: "numeric",
  }).format(date);
}

/** Test hook -- clears the memo caches so a test can switch locales cleanly. */
export function clearFormatCaches(): void {
  formatterCache.clear();
  monthNameCache.clear();
  weekdayNameCache.clear();
}
